import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Camera, Upload, Sparkles } from "lucide-react";

import { useAuth } from "../AuthContext";
import { api } from "../api";
import { t } from "../i18n";
import CropOverlay from "../components/CropOverlay";

const TYPE_CONFIG = {
  hint:     { endpoint: "/generate/math-hint",     titleKey: "math_hint",     priceKey: "hint_price",     expectedSec: 65, processingKey: "generating_hint" },
  solution: { endpoint: "/generate/math-solution", titleKey: "math_solution", priceKey: "solution_price", expectedSec: 15, processingKey: "generating_solution" },
};

function ProcessingScreen({ lang, expectedSec, processingKey }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  return (
    <div className="page-shell flex flex-col items-center justify-center text-center" data-testid="math-processing">
      <motion.div
        animate={{ y: [0, -22, 0], rotate: [0, -10, 10, 0] }}
        transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
        className="w-32 h-32 rounded-full bg-gradient-to-br from-violet-400 to-pink-400 flex items-center justify-center shadow-[0_10px_0_rgba(0,0,0,0.12)] border-4 border-white"
      >
        <Sparkles size={56} strokeWidth={2.5} className="text-white" />
      </motion.div>
      <h2 className="font-heading text-3xl font-bold mt-8 text-white drop-shadow-[0_3px_10px_rgba(15,23,42,0.55)]">
        {t(lang, processingKey)}
      </h2>
      <p className="text-base text-cyan-50/95 mt-2 font-semibold drop-shadow-[0_2px_8px_rgba(15,23,42,0.45)]">{t(lang, "almost_there")}</p>
      <div className="mt-8 bg-white rounded-3xl px-8 py-4 border-4 border-slate-900/10 shadow-[0_6px_0_rgba(0,0,0,0.08)]">
        <div className="font-heading text-5xl font-bold text-slate-800 tabular-nums">{mm}:{ss}</div>
        <p className="text-xs text-slate-500 font-bold mt-1">usually ~{expectedSec}s</p>
      </div>
    </div>
  );
}

export default function MathCapture() {
  const { type } = useParams();
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.hint;
  const navigate = useNavigate();
  const { lang, updateBalance } = useAuth();

  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);

  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [captured, setCaptured] = useState(null); // {url}
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 } },
          audio: false,
        });
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setCameraReady(true);
      } catch (e) {
        setCameraError(e.message || "Camera not available");
      }
    })();
    return () => {
      active = false;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const snap = () => {
    if (!videoRef.current) return;
    const v = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth || 720;
    canvas.height = v.videoHeight || 1280;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        setCaptured({ url });
        stopCamera();
      },
      "image/jpeg",
      0.92
    );
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCaptured({ url: URL.createObjectURL(f) });
    stopCamera();
  };

  const retake = () => {
    if (captured?.url) URL.revokeObjectURL(captured.url);
    setCaptured(null);
    // Try to restart camera
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 } },
          audio: false,
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setCameraReady(true);
      } catch (_e) { /* ignore — upload still available */ }
    })();
  };

  const submitCropped = async (croppedBlob) => {
    if (!croppedBlob || processing) return;
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append("file", croppedBlob, "math.jpg");
      if (type === "hint") fd.append("language", lang);
      const r = await api.post(cfg.endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (typeof r.data.wallet_balance === "number") updateBalance(r.data.wallet_balance);
      stopCamera();
      try {
        sessionStorage.setItem("dt_math_result", JSON.stringify(r.data));
      } catch (_) { /* ignore quota */ }
      navigate("/math/result", { replace: true });
    } catch (e) {
      const detail = e.response?.data?.detail || "Generation failed";
      toast.error(detail);
      setProcessing(false);
    }
  };

  if (processing) return <ProcessingScreen lang={lang} expectedSec={cfg.expectedSec} processingKey={cfg.processingKey} />;

  return (
    <div className="page-shell" data-testid="math-capture-page">
      <div className="flex items-center justify-between mb-4">
        <button
          data-testid="back-btn"
          onClick={() => navigate("/math")}
          className="p-3 rounded-full bg-white border-2 border-slate-900/10 btn-squish"
        >
          <ArrowLeft size={22} strokeWidth={2.5} className="text-slate-700" />
        </button>
        <h2 className="font-heading text-2xl font-bold text-white drop-shadow-[0_3px_10px_rgba(15,23,42,0.55)]">{t(lang, cfg.titleKey)}</h2>
        <div className="w-12" />
      </div>

      {captured ? (
        <CropOverlay
          src={captured.url}
          onConfirm={(blob) => submitCropped(blob)}
          onCancel={retake}
          confirmLabel={t(lang, "use_cropped_area")}
          cancelLabel={t(lang, "retake")}
        />
      ) : (
        <>
          <div className="relative aspect-[3/4] w-full bg-slate-900 rounded-3xl overflow-hidden border-4 border-white shadow-[0_8px_0_rgba(0,0,0,0.12)]">
            <video
              ref={videoRef}
              data-testid="math-camera-video"
              playsInline
              muted
              autoPlay
              className="w-full h-full object-cover"
            />
            <div className="pointer-events-none absolute inset-6 border-4 border-dashed border-white/70 rounded-2xl" />
            {!cameraReady && !cameraError && (
              <div className="absolute inset-0 flex items-center justify-center text-white text-lg font-bold">
                Starting camera...
              </div>
            )}
            {cameraError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-6 text-center">
                <p className="font-bold">Camera unavailable</p>
                <p className="text-sm opacity-80 mt-1">{cameraError}</p>
                <p className="text-sm mt-3">You can upload a picture below 👇</p>
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={pickFile}
            className="hidden"
            data-testid="math-file-input"
          />

          <div className="mt-6 flex flex-col gap-3">
            <button
              data-testid="math-snap-btn"
              disabled={!cameraReady}
              onClick={snap}
              className="w-full bg-pink-400 hover:bg-pink-500 disabled:bg-slate-200 disabled:text-slate-400 text-white font-heading text-xl font-bold uppercase tracking-wide py-4 rounded-full btn-squish flex items-center justify-center gap-2"
            >
              <Camera size={24} strokeWidth={2.5} /> {t(lang, "take_photo")}
            </button>
            <button
              data-testid="math-upload-btn"
              onClick={() => fileInputRef.current?.click()}
              className="w-full bg-white border-2 border-slate-900/10 text-slate-700 font-bold py-3 rounded-full flex items-center justify-center gap-2 btn-squish"
            >
              <Upload size={20} strokeWidth={2.5} /> {t(lang, "upload_from_gallery")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
